import { Module, ActionTree, GetterTree, MutationTree, ActionContext } from 'vuex';
import { ResourceState, RootState } from './types';

import { SbixService } from '@/api/SbixService';
import { SysService } from '@/api/SysService';
import { EtcService } from '@/api/EtcService';
import { NewsFeedService } from '@/api/NewsFeedService';
import { FeatureFlagsService } from '@/api/FeatureFlagsService';
import { MoratoriumService } from '@/api/MoratoriumService';
import { UnderwriterService } from '@/api/UnderwriterService';
import { RestrictedFeatureService } from '@/api/RestrictedFeatureService';

import testDataModule from '@/vuex/testDataModule';
import { Amwins_PersonalLines_Moratorium_AdminUI_Generated_Moratorium } from '@/api/index.defs';

export const state: ResourceState = {
  catastropheClosings: [],
  moratoriumInEffect: false,
  moratoriumBannerText: "",
  countries: [],
  countriesV1: [],
  moratoriums: [],
  dashboardNews: [],
  underwriters: [],
  restrictedFeatures: [],
  floodDiligentEffortsData: [],
  homeownersDiligentEffortsData: [],
  loading: false
};

const mutations = {
  setLoading: (state: any, payload: any) => {
    state.loading = payload;
  },
  setCatastropheClosings: (state: any, items: any) => {
    state.catastropheClosings = items;
  },
  setMoratoriumInEffect: (state: any, moratoriumInEffect: boolean) => {
    state.moratoriumInEffect = moratoriumInEffect;
  },
  setMoratoriumBannerText: (state: any, moratoriumBannerText: string) => {
    state.moratoriumBannerText = moratoriumBannerText;
  },
  setCountries: (state: any, items: any[]) => {
    state.countries = items;
  },
  setCountriesV1: (state: any, items: any[]) => {
    state.countriesV1 = items;
  },
  setAllActiveMoratoriums: (state: any, items: any[]) => {
    state.moratoriums = items;
  },
  setUnderwriters: (state: any, items: any[]) => {
    state.underwriters = items;
  },
  addDashboardNewsItem: (state: any, item: any) => {
    state.dashboardNews.push(item);
  },
  setRestrictedFeatures: (state: any, item: any) => {
    state.restrictedFeatures = item;
  },
  setFloodDiligentEffortsData: (state: any, items: any[]) => {
    state.floodDiligentEffortsData = items;
  },
  setHomeownersDiligentEffortsData: (state: any, items: any[]) => {
    state.homeownersDiligentEffortsData = items;
  }
};

const getters = {
  loading (state: any) {
    return state.loading;
  },
  // Returns the moratorium banner display text
  moratoriumBannerDisplayText (state: any) {
    return state.moratoriumBannerText;
  },
  // Returns boolean that determines if there are any current moratoriums
  isCurrentMoratorium (state: any) {
    return state.moratoriumInEffect;
  },
  // Returns array of objects for all closings
  getItems (state: any) {
    return state.catastropheClosings;
  },
  // Returns array of objects for any current closings for a specific risk
  currentClosingsForRisk: (state: any) => (postalCode: any, stateAbbr: any, county: any, program: any) => {
    const now = new Date();
    return state.catastropheClosings.filter((x: { EffectiveDateTime: Date, ExpirationDateTime: Date, IsCurrent: boolean, State: string, Zip: string, County: string, Programs: string }) =>
      // x.IsCurrent &&
      (now >= new Date(x.EffectiveDateTime)) &&
      (now <= new Date(x.ExpirationDateTime)) &&
      (x.State.includes('%') || x.State.includes(stateAbbr)) &&
      (x.Zip.includes(`%(${stateAbbr})`) || x.Zip.includes(postalCode)) &&
      (x.County.includes(`%(${stateAbbr})`) || x.County.includes(county)) &&
      (x.Programs.includes('%') || x.Programs.includes(program)));
  },
  getCountries (state: any) {
    return state.countries;
  },
  getCountriesV1 (state: any) {
    return state.countriesV1;
  },
  getAllActiveMoratoriums (state: any) {
    return state.moratoriums;
  },
  getUnderwriters (state: any) {
    return state.underwriters;
  },
  getDashboardNews (state: any) {
    return state.dashboardNews;
  },
  getRestrictedFeatures (state: any) {
    return state.restrictedFeatures;
  },
  getFloodDiligentEffortsData (state: any) {
    return state.floodDiligentEffortsData;
  },
  getHomeownersDiligentEffortsData (state: any) {
    return state.homeownersDiligentEffortsData;
  }
};

const actions: ActionTree<ResourceState, RootState> = {
  getMoratoriumBanner: ({ commit }: ActionContext<ResourceState, RootState>) => {
    // if env local, use mock data
    const isLocalEnvironment = process.env.NODE_ENV === 'development';
    if (isLocalEnvironment) {
      commit('setMoratoriumInEffect', testDataModule.state.moratoriumTestBanner.MoratoriumInEffect);
      commit('setMoratoriumBannerText', testDataModule.state.moratoriumTestBanner.BannerDisplayText);
    } else {
    // if env not local, make real request


            try {
              MoratoriumService.banner()
              .then((response) => {
                commit('setMoratoriumInEffect', response.MoratoriumInEffect);
                commit('setMoratoriumBannerText', response.BannerDisplayText);
              });
            } catch (error) {
              // Handle the error
              console.error(error);
            }
    }
  },
  getCatastropheClosings: ({ commit, dispatch }) => {
    commit('setLoading', true);
    SbixService.getCatastrophies()
      .then((response) => {
        commit('setCatastropheClosings', response);
      })
      .catch((e) => {
        console.warn('unexpected error in getCatastropheClosings, will retry in 10secs...', e);
        setTimeout(() => {
          dispatch('getCatastropheClosings');
        }, 10000);
      })
      .finally(() => commit('setLoading', false));
  },
  getCountries ({ commit }) {
    SysService.country()
      .then((response) => {
        commit('setCountries', response);
      })
      .catch((e) => {
        console.log('error', e);
      });
  },
  getCountriesV1 ({ commit }) {
    EtcService.iso3166()
      .then((response) => {
        commit('setCountriesV1', response);
      })
      .catch((e) => {
        console.log('error', e);
      });
  },
  getAllActiveMoratoriums ({ commit }) {
    return MoratoriumService.getMoratoriums()
      .then((response) => {
        let activeMoratoriums = response.filter(x => x.active === true);
        commit('setAllActiveMoratoriums', activeMoratoriums);
      })
      .catch((e) => {
        console.log('error', e);
      });
  },
  getUnderwriters ({ commit }) {
    const zips = require("zips");

    UnderwriterService.getAllUnderwriters()
      .then((response) => {
        response.forEach(underwriter => {
          if (!underwriter.GeoLatitude || !underwriter.GeoLongitude) {
            const place = zips.getByZipCode(underwriter.Postal);

            underwriter.GeoLatitude = place.lat;
            underwriter.GeoLongitude = place.long;
          }
        });
        commit('setUnderwriters', response);
      })
      .catch((e) => {
        console.log('error', e);
      });
  },
  getDashboardNews ({ state, commit }) {
    if (state.dashboardNews.length > 0) return;
    NewsFeedService.getAmwinsArticles({
      articleCount: 5
    }, {
      // @ts-ignore
      handleError: false
    }).then((response) => {
        if (!response) return; // handle 204 response
        response.value.forEach((article: any) => {
          NewsFeedService.getAmwinsArticleImage({
            key: article.Id
          }, {
            // @ts-ignore
            handleError: false
          }).then((imageResponse) => {
              if (imageResponse.value.length === 0) {
                return;
              }

              article.ImageUrl = imageResponse.value[0].Url;
              article.AbstractForDisplay = article.Abstract.substring(0, 120) + "...";
              commit('addDashboardNewsItem', article);
            });
        });
      });
  },
  async getRestrictedFeatures ({ state, commit }) {
    // TODO: add a timeout to the cache if needed
    if (state.restrictedFeatures?.length) return state.restrictedFeatures;
    return await RestrictedFeatureService.getList()
      .then((response) => {
        commit('setRestrictedFeatures', response);
        return response;
      })
      .catch((e) => {
        console.log('error', e);
      });
  },
  async getFloodDiligentEffortsData ({ state, commit }) {
    if (state.floodDiligentEffortsData?.length) return state.floodDiligentEffortsData;
    return await SysService.diligentEffort({ product: "BindV1" })
        .then((data) => {
          commit('setFloodDiligentEffortsData', data);
          return data;
        })
      .catch((e) => {
        console.log('error', e);
      });
  },
  async getHomeownersDiligentEffortsData ({ state, commit }) {
    if (state.homeownersDiligentEffortsData?.length) return state.homeownersDiligentEffortsData;
    return await SysService.diligentEffort({ product: "BindV2" })
        .then((data) => {
          commit('setHomeownersDiligentEffortsData', data);
          return data;
        })
      .catch((e) => {
        console.log('error', e);
      });
  }
};

const namespaced: boolean = true;

export const resource: Module<ResourceState, RootState> = {
    namespaced,
    state,
    getters,
    actions,
    mutations
};
